import React, { useState } from 'react';
import axios from 'axios';
import { Building2, Store } from 'lucide-react';
import config from '../../config';
import CadreEtape from './CadreEtape';

const API_URL = config.API_URL;

/**
 * Première étape : pour qui cette personne travaille.
 *
 * ## Pourquoi la poser avant tout le reste
 *
 * Le modèle de données est uniformément celui d'une agence : tout compte a un niveau `Client`
 * au-dessus de ses sites. Un annonceur qui gère sa propre société doit donc créer un « client »
 * qui est lui-même — geste incompréhensible si on le lui demande sans préparation. Connaître sa
 * réponse permet de nommer l'étape suivante avec ses mots à lui : « votre société » plutôt que
 * « votre premier client ».
 *
 * La réponse est conservée (`User.typeCompte`) et non tenue en mémoire : elle servira à alléger
 * le vocabulaire ailleurs dans l'application, là où la notion de client n'apporte rien à un
 * annonceur seul.
 */
function EtapeTypeCompte({ token, onAvance }) {
  const [envoi, setEnvoi] = useState(null);
  const [erreur, setErreur] = useState(null);

  const choisir = async (typeCompte) => {
    setEnvoi(typeCompte);
    setErreur(null);
    try {
      await axios.put(`${API_URL}/api/onboarding/type-compte`, { typeCompte }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await onAvance();
    } catch (e) {
      setErreur(e.response?.data?.error || "Impossible d'enregistrer votre réponse");
      setEnvoi(null);
    }
  };

  const choix = [
    {
      cle: 'ANNONCEUR',
      icone: Store,
      titre: 'Pour ma société',
      detail: 'Je suis annonceur et je suis mes propres campagnes.'
    },
    {
      cle: 'AGENCE',
      icone: Building2,
      titre: 'Pour mes clients',
      detail: 'Je suis une agence ou un consultant et je gère plusieurs annonceurs.'
    }
  ];

  return (
    <CadreEtape
      titre="Vous utilisez Kokpit…"
      explication="Cela nous sert à nommer les choses comme vous le faites. Vous pourrez toujours suivre plusieurs annonceurs."
      erreur={erreur}
      largeur="560px"
    >
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '14px' }}>
        {choix.map(({ cle, icone: Icone, titre, detail }) => (
          <button
            key={cle}
            onClick={() => choisir(cle)}
            disabled={Boolean(envoi)}
            style={{
              textAlign: 'left', padding: '20px', borderRadius: '12px',
              border: '1px solid #e5e7eb', background: 'white',
              cursor: envoi ? 'wait' : 'pointer',
              opacity: envoi && envoi !== cle ? 0.5 : 1
            }}
          >
            <div style={{
              width: '40px', height: '40px', borderRadius: '10px', background: '#1a1a1a',
              display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '12px'
            }}>
              <Icone size={20} color="white" />
            </div>
            <div style={{ fontSize: '15px', fontWeight: 600, color: '#1f2937', marginBottom: '4px' }}>
              {titre}
            </div>
            <div style={{ fontSize: '13px', color: '#6b7280', lineHeight: 1.5 }}>{detail}</div>
          </button>
        ))}
      </div>
    </CadreEtape>
  );
}

export default EtapeTypeCompte;
